import { createStrictContext } from 'utils/createContext'
import { Issue, IssueTrackerContext } from './types'
import {
  useEditIssueMutation,
  useIssueTrackerIssuesQuery,
  useIssueTrackerRelationsQuery,
} from 'schema'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import { useCIMSModalValues } from 'components/CIMSModal/hooks/useCIMSModal'
import { useEffect, useState } from 'react'
import { WithPayItemGlobalId } from 'components/CIMSTable/components/HeaderActionButtons/types'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { useProjectContext } from 'context/ProjectContext'

const { Provider, useContextFn } = createStrictContext<IssueTrackerContext>(
  'IssueTrackerContext'
)

export const useIssueTrackerContext = useContextFn

const IssueTrackerProvider: React.FC<WithPayItemGlobalId> = ({
  children,
  payItemGlobalId,
}) => {
  const { projectId } = useProjectContext()
  const {
    data: issuesData,
    loading: issuesLoading,
    refetch: refetchIssues,
  } = useIssueTrackerIssuesQuery({
    ...DefaultQueryHookOptions,
    variables: {
      input: { projectId: { equals: projectId } },
      projectId,
    },
  })
  const {
    data: relationsData,
    loading: relationsLoading,
    refetch: refetchRelations,
  } = useIssueTrackerRelationsQuery({
    ...DefaultQueryHookOptions,
    skip: !issuesData?.issues?.records,
    variables: {
      projectId,
      issueIds: issuesData?.issues?.records?.map(({ id }) => id) ?? [],
    },
  })
  const [issues, setIssues] = useState(Array<Issue>())
  useEffect(() => {
    const comments = relationsData?.issueComments?.records?.reduce(
      (map, { issueId, ...comment }) =>
        map.set(issueId, [...(map.get(issueId) ?? []), comment]),
      new Map<string, Issue['comments']>()
    )
    const payItems = relationsData?.issueToPayItemNoGeometries?.records?.reduce(
      (map, { issueId, payItem }) =>
        map.set(issueId, [...(map.get(issueId) ?? []), payItem]),
      new Map<string, Issue['payItems']>()
    )
    const reports = relationsData?.issueToReports?.records?.reduce(
      (map, { issueId, ...report }) =>
        map.set(issueId, [...(map.get(issueId) ?? []), report]),
      new Map<string, Issue['reports']>()
    )
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
    setIssues(
      (issuesData?.issues?.records ?? []).map<Issue>((issue) => ({
        ...issue,
        comments: comments?.get(issue.id) ?? [],
        payItems: payItems?.get(issue.id) ?? [],
        reports: reports?.get(issue.id) ?? [],
      }))
    )
  }, [
    issuesData?.issues?.records,
    relationsData?.issueComments?.records,
    relationsData?.issueToPayItemNoGeometries?.records,
    relationsData?.issueToReports?.records,
  ])
  const [edit] = useEditIssueMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editIssue: {
        issue: { formattedNumber },
      },
    }) => simpleEditNotification(formattedNumber),
  })
  return (
    <Provider
      value={{
        quickLinkOptions: {
          quickLinks: relationsData?.issueQuickLinks?.records ?? [],
          loading: relationsLoading,
        },
        payItemGlobalId,
        issues: { loading: issuesLoading, refetch: refetchIssues, issues },
        relations: { loading: relationsLoading, refetch: refetchRelations },
        draftSubmitModal: useCIMSModalValues(),
        actionModal: useCIMSModalValues(),
        revertModal: useCIMSModalValues(),
        edit: async (input) => {
          await edit({ variables: { input } })
          refetchIssues()
          refetchRelations()
        },
      }}
    >
      {children}
    </Provider>
  )
}

export default IssueTrackerProvider
